import { useState, useMemo, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import type { PipelineCard as PipelineCardType, LostReason } from '@1130-studio/shared'
import { usePipeline, useMovePipelineCard, useUpdateLostReason, useUpdatePipelineCard } from '../api/pipeline'
import { useDeleteLead } from '../api/leads'
import { useDeleteProject } from '../api/projects'
import { useAuthStore } from '../store/authStore'
import { queryKeys } from '../lib/query'
import { cn } from '../lib/utils'
import {
  PipelineColumn,
  PipelineCardDragOverlay,
  CardDetailModal,
  TransitionModal,
  LostReasonModal,
  ArchiveSection,
} from '../components/pipeline'

type PipelineFilter = 'all' | 'mine' | 'priority' | 'deadline'

const filterLabels: Record<PipelineFilter, string> = {
  all: 'Все',
  mine: 'Мои',
  priority: 'Приоритетные',
  deadline: 'Дедлайн скоро',
}

export function PipelinePage() {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [localCards, setLocalCards] = useState<Record<string, PipelineCardType[]>>({})
  const [activeFilter, setActiveFilter] = useState<PipelineFilter>('all')
  const currentUser = useAuthStore((s) => s.user)

  // Modal states
  const [selectedCard, setSelectedCard] = useState<PipelineCardType | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showTransitionModal, setShowTransitionModal] = useState(false)
  const [showLostReasonModal, setShowLostReasonModal] = useState(false)
  const [pendingTransition, setPendingTransition] = useState<{
    card: PipelineCardType
    targetStageId: string
    position: number
  } | null>(null)
const [pendingLostCard, setPendingLostCard] = useState<PipelineCardType | null>(null)

  const queryClient = useQueryClient()
  const { data: pipelineData, isLoading, error } = usePipeline()
  const moveCard = useMovePipelineCard()
  const updateLostReason = useUpdateLostReason()
  const updateCard = useUpdatePipelineCard()
  const deleteLead = useDeleteLead()
  const deleteProject = useDeleteProject()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Sync local cards with server data
  useMemo(() => {
    if (pipelineData && !moveCard.isPending) {
      setLocalCards(pipelineData.cards)
    }
  }, [pipelineData?.cards, moveCard.isPending])

  // Filter cards based on active filter
  const filterCard = useCallback((card: PipelineCardType): boolean => {
    if (activeFilter === 'all') return true
    if (activeFilter === 'mine') return card.assignedTo === currentUser?.id
    if (activeFilter === 'priority') return card.priority === 'high' || card.priority === 'urgent'
    if (activeFilter === 'deadline') {
      if (!card.deadline) return false
      const deadline = new Date(card.deadline)
      const now = new Date()
      const daysUntil = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      return daysUntil >= 0 && daysUntil <= 7
    }
    return true
  }, [activeFilter, currentUser?.id])

  const filteredCards = useMemo(() => {
    if (activeFilter === 'all') return localCards
    const result: Record<string, PipelineCardType[]> = {}
    for (const [stageId, cards] of Object.entries(localCards)) {
      result[stageId] = cards.filter(filterCard)
    }
    return result
  }, [localCards, filterCard, activeFilter])

  // Find card across all stages
  const findCard = (id: string): PipelineCardType | undefined => {
    for (const stageCards of Object.values(localCards)) {
      const card = stageCards.find((c) => c.id === id)
      if (card) return card
    }
    return undefined
  }

  // Find which stage a card is in
  const findContainer = (id: string): string | undefined => {
    // First check if id is a stage id
    if (pipelineData?.stages.some((s) => s.id === id)) {
      return id
    }

    // Otherwise find the card's container
    for (const [stageId, cards] of Object.entries(localCards)) {
      if (cards.some((c) => c.id === id)) {
        return stageId
      }
    }
    return undefined
  }

  const activeCard = useMemo(() => {
    if (!activeId) return undefined
    return findCard(activeId)
  }, [activeId, localCards])

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event

    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string

    const activeContainer = findContainer(activeId)
    const overContainer = findContainer(overId)

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return
    }

    setLocalCards((prev) => {
      const activeItems = [...(prev[activeContainer] || [])]
      const overItems = [...(prev[overContainer] || [])]

      const activeIndex = activeItems.findIndex((c) => c.id === activeId)
      const movedCard = activeItems[activeIndex]

      // Remove from source
      activeItems.splice(activeIndex, 1)

      // Add to destination
      const overIndex = overItems.findIndex((c) => c.id === overId)
      const newIndex = overIndex >= 0 ? overIndex : overItems.length

      overItems.splice(newIndex, 0, { ...movedCard, stageId: overContainer })

      return {
        ...prev,
        [activeContainer]: activeItems,
        [overContainer]: overItems,
      }
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    setActiveId(null)

    if (!over || !pipelineData) {
      // Reset to original data if dropped outside
      setLocalCards(pipelineData?.cards || {})
      return
    }

    const activeId = active.id as string
    const overId = over.id as string

    const activeContainer = findContainer(activeId)
    let overContainer = findContainer(overId)

    // If dropping on a stage directly (empty column)
    if (pipelineData.stages.some((s) => s.id === overId)) {
      overContainer = overId
    }

    if (!activeContainer || !overContainer) {
      setLocalCards(pipelineData.cards)
      return
    }

    // Get card and stage info
    const card = findCard(activeId)
    const sourceStage = pipelineData.stages.find((s) => s.id === activeContainer)
    const targetStage = pipelineData.stages.find((s) => s.id === overContainer)

    if (!card || !targetStage) {
      setLocalCards(pipelineData.cards)
      return
    }

    // Calculate new position
    const overItems = localCards[overContainer] || []
    const overIndex = overItems.findIndex((c) => c.id === overId)
    const position = overIndex >= 0 ? overIndex : overItems.length

    // Check for lead → project transition
    if (
      card.type === 'lead' &&
      sourceStage?.type === 'lead' &&
      targetStage.type === 'project'
    ) {
      // Show transition modal
      setPendingTransition({ card, targetStageId: overContainer, position })
      setShowTransitionModal(true)
      // Reset cards until confirmed
      setLocalCards(pipelineData.cards)
      return
    }

    // Check for archive (lost) transition
    if (card.type === 'lead' && targetStage.type === 'archive') {
      // Show lost reason modal
      setPendingLostCard(card)
      setPendingTransition({ card, targetStageId: overContainer, position })
      setShowLostReasonModal(true)
      return
    }

    // Prevent projects from moving to lead stages
    if (card.type === 'project' && targetStage.type === 'lead') {
      setLocalCards(pipelineData.cards)
      return
    }

    // Regular move
    moveCard.mutate({
      id: activeId,
      data: {
        type: card.type,
        targetStageId: overContainer,
        position,
      },
    })
  }

  const handleTransitionConfirm = (projectName: string) => {
    if (!pendingTransition) return

    moveCard.mutate(
      {
        id: pendingTransition.card.id,
        data: {
          type: 'lead',
          targetStageId: pendingTransition.targetStageId,
          position: pendingTransition.position,
          projectName,
        },
      },
      {
        onSuccess: () => {
          setShowTransitionModal(false)
          setPendingTransition(null)
        },
      }
    )
  }

  const handleLostReasonConfirm = (reason: LostReason, comment?: string) => {
    if (!pendingLostCard || !pendingTransition) return

    // First move the card
    moveCard.mutate(
      {
        id: pendingTransition.card.id,
        data: {
          type: 'lead',
          targetStageId: pendingTransition.targetStageId,
          position: pendingTransition.position,
        },
      },
      {
        onSuccess: () => {
          // Then update the lost reason
          updateLostReason.mutate({
            id: pendingLostCard.id,
            data: { lostReason: reason, lostComment: comment },
          })
          setShowLostReasonModal(false)
          setPendingLostCard(null)
          setPendingTransition(null)
        },
      }
    )
  }

  const handleCardClick = (card: PipelineCardType) => {
    setSelectedCard(card)
    setShowDetailModal(true)
  }

const handleCardSave = async (id: string, data: { specification?: string; notes?: string }) => {
    const card = findCard(id)
    if (!card) return

    await updateCard.mutateAsync({
      id,
      data: {
        type: card.type,
        specification: data.specification,
        notes: data.notes,
      },
    })
  }

  const handleCardDelete = async (id: string, type: 'lead' | 'project') => {
    if (type === 'lead') {
      await deleteLead.mutateAsync(id)
    } else {
      await deleteProject.mutateAsync(id)
    }
    // Invalidate pipeline to refresh the board
    queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.all })
    setShowDetailModal(false)
    setSelectedCard(null)
  }

  // Separate stages by type
  const leadStages = pipelineData?.stages.filter((s) => s.type === 'lead') || []
  const projectStages = pipelineData?.stages.filter((s) => s.type === 'project') || []
  const archiveStage = pipelineData?.stages.find((s) => s.type === 'archive')

  // Calculate stats
  const totalLeads = leadStages.reduce(
    (sum, stage) => sum + (localCards[stage.id]?.length || 0),
    0
  )
  const totalProjects = projectStages.reduce(
    (sum, stage) => sum + (localCards[stage.id]?.length || 0),
    0
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-secondary">Загрузка...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">Ошибка загрузки пайплайна</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="px-8 py-5 border-b border-border-subtle bg-bg-secondary sticky top-0 z-50 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-2xl font-bold tracking-tight">Pipeline</h1>
          <div className="flex gap-4">
            <div className="flex items-center gap-2 px-3.5 py-1.5 bg-accent-orange/10 rounded-full text-[13px] text-accent-orange">
              <strong className="font-semibold">{totalLeads}</strong> заявок
            </div>
            <div className="flex items-center gap-2 px-3.5 py-1.5 bg-accent-blue/10 rounded-full text-[13px] text-accent-blue">
              <strong className="font-semibold">{totalProjects}</strong> проектов
            </div>
          </div>
        </div>
        <div className="flex gap-1.5">
          {(Object.keys(filterLabels) as PipelineFilter[]).map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={cn(
                'px-3 py-1.5 text-xs rounded-lg transition-colors border',
                activeFilter === filter
                  ? 'bg-white text-black border-white'
                  : 'bg-white/5 text-text-secondary border-white/10 hover:text-white hover:border-white/20'
              )}
            >
              {filterLabels[filter]}
            </button>
          ))}
        </div>
      </header>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-x-auto px-8 py-6 bg-gradient-to-br from-accent-orange/[0.03] via-transparent to-accent-blue/[0.03]">
          <div className="flex gap-5 min-w-max pb-6">
            {/* Lead Stages */}
            <div className="flex gap-5">
              {leadStages.map((stage, index) => (
                <PipelineColumn
                  key={stage.id}
                  stage={{ ...stage, type: index === 0 ? 'lead' : stage.type } as typeof stage}
                  cards={filteredCards[stage.id] || []}
                  onCardClick={handleCardClick}
                />
              ))}
            </div>

            {/* Divider */}
            <div className="w-px bg-white/10 mx-2 self-stretch" />

            {/* Project Stages */}
            <div className="flex gap-5">
              {projectStages.map((stage, index) => (
                <PipelineColumn
                  key={stage.id}
                  stage={{ ...stage, type: index === 0 ? 'project' : stage.type } as typeof stage}
                  cards={filteredCards[stage.id] || []}
                  onCardClick={handleCardClick}
                />
              ))}
            </div>
          </div>

          {/* Archive Section */}
          {archiveStage && (
            <ArchiveSection
              stage={archiveStage}
              cards={filteredCards[archiveStage.id] || []}
              onCardClick={handleCardClick}
            />
          )}
        </div>

        <DragOverlay>
          {activeCard ? <PipelineCardDragOverlay card={activeCard} /> : null}
        </DragOverlay>
      </DndContext>

{/* Modals */}
      <CardDetailModal
        card={selectedCard}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false)
          setSelectedCard(null)
        }}
        onSave={handleCardSave}
        onDelete={handleCardDelete}
      />

      <TransitionModal
        card={pendingTransition?.card || null}
        isOpen={showTransitionModal}
        onClose={() => {
          setShowTransitionModal(false)
          setPendingTransition(null)
          if (pipelineData) {
            setLocalCards(pipelineData.cards)
          }
        }}
        onConfirm={handleTransitionConfirm}
        isLoading={moveCard.isPending}
      />

      <LostReasonModal
        isOpen={showLostReasonModal}
        onClose={() => {
          setShowLostReasonModal(false)
          setPendingLostCard(null)
          setPendingTransition(null)
          if (pipelineData) {
            setLocalCards(pipelineData.cards)
          }
        }}
        onConfirm={handleLostReasonConfirm}
        isLoading={moveCard.isPending || updateLostReason.isPending}
      />
    </div>
  )
}
